import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ELEMENT_TYPES } from "../constants/boqElementTypes";

/** Right-click context menu for a BoQ grid row. Items are contextual based on row type.
 *  onAction(rowId, elementType, placement) is called when an item is clicked. */
export function BoQRowContextMenu({ x, y, row, parentRow, onClose, onAction, t }) {
    const menuRef = useRef(null);
    const type = row.original?.type;

    // Close on click-outside or Escape
    useEffect(() => {
        const onMouseDown = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
        };
        const onKeyDown = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [onClose]);

    // Viewport-edge awareness: shift left/up if menu would overflow
    const [pos, setPos] = useState({ left: x, top: y });
    useLayoutEffect(() => {
        const el = menuRef.current;
        if (!el) return;
        const { width, height } = el.getBoundingClientRect();
        setPos({
            left: x + width > window.innerWidth ? x - width : x,
            top: y + height > window.innerHeight ? y - height : y,
        });
    }, [x, y]);

    const insideItems = [];
    const afterItems = [];

    if (type === ELEMENT_TYPES.GROUP) {
        insideItems.push(
            { elementType: ELEMENT_TYPES.SUBGROUP, labelKey: "boq.add.subgroup" },
            { elementType: ELEMENT_TYPES.POSITION, labelKey: "boq.add.position" },
            { elementType: ELEMENT_TYPES.NOTE, labelKey: "boq.add.hint-text" },
        );
        afterItems.push({ elementType: ELEMENT_TYPES.GROUP, labelKey: "boq.add.group" });
    } else if (type === ELEMENT_TYPES.SUBGROUP) {
        insideItems.push(
            { elementType: ELEMENT_TYPES.POSITION, labelKey: "boq.add.position" },
            { elementType: ELEMENT_TYPES.NOTE, labelKey: "boq.add.hint-text" },
        );
        afterItems.push({ elementType: ELEMENT_TYPES.SUBGROUP, labelKey: "boq.add.subgroup" });
    } else {
        afterItems.push(
            { elementType: ELEMENT_TYPES.POSITION, labelKey: "boq.add.position" },
            { elementType: ELEMENT_TYPES.NOTE, labelKey: "boq.add.hint-text" },
        );
        const parentType = parentRow?.original?.type;
        if (parentType === ELEMENT_TYPES.SUBGROUP) {
            afterItems.push({ elementType: ELEMENT_TYPES.SUBGROUP, labelKey: "boq.add.subgroup", targetId: parentRow.original.id, placement: "after" });
        } else if (parentType === ELEMENT_TYPES.GROUP) {
            afterItems.push({ elementType: ELEMENT_TYPES.SUBGROUP, labelKey: "boq.add.subgroup", targetId: parentRow.original.id, placement: "inside" });
        }
    }

    return (
        <div
            ref={menuRef}
            className="boq-context-menu"
            style={{ left: pos.left, top: pos.top }}
            onContextMenu={(e) => e.preventDefault()}
        >
            {insideItems.length > 0 && (
                <>
                    <div className="boq-context-menu__section-label">{t("boq.context-menu.add-inside")}</div>
                    {insideItems.map((item) => (
                        <button
                            key={item.elementType}
                            type="button"
                            className="boq-context-menu__item"
                            onClick={() => { onClose(); onAction(row.original.id, item.elementType, "inside"); }}
                        >
                            <span className={`boq-option-swatch boq-option-swatch--${item.elementType?.replace(/_/g, "-")}`} />
                            {t(item.labelKey)}
                        </button>
                    ))}
                </>
            )}
            {insideItems.length > 0 && afterItems.length > 0 && (
                <div className="boq-context-menu__separator" />
            )}
            {afterItems.length > 0 && (
                <>
                    <div className="boq-context-menu__section-label">{t("boq.context-menu.add-after")}</div>
                    {afterItems.map((item) => (
                        <button
                            key={item.elementType}
                            type="button"
                            className="boq-context-menu__item"
                            onClick={() => { onClose(); onAction(item.targetId ?? row.original.id, item.elementType, item.placement ?? "after"); }}
                        >
                            <span className={`boq-option-swatch boq-option-swatch--${item.elementType?.replace(/_/g, "-")}`} />
                            {t(item.labelKey)}
                        </button>
                    ))}
                </>
            )}
        </div>
    );
}
